/* Cube 3D - homepage behaviour (2026-10-19).
 * <cube-hero>: scroll-snap carousel (native swipe) with discreet dots; the current dot fills as a timer and the
 *   slide changes when it is full (Bambu-style). No arrows; with a mouse the slides can be dragged.
 *   The timer pauses while a mouse is over the hero or keyboard focus is in it, while
 *   it is off screen or the tab is hidden, and autoplay never runs with reduced motion. Slides that are not shown
 *   are inert, so keyboard users only tab through the visible slide.
 * <cube-tabs>: role=tab buttons with Left/Right/Home/End keys (printer showcase families).
 * Theme editor: selecting a slide or a family block shows it.
 */
(function () {
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!customElements.get('cube-hero')) {
    class CubeHero extends HTMLElement {
      connectedCallback() {
        var self = this;
        this.track = this.querySelector('[data-hero-track]');
        this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-hero-slide]'));
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-hero-dot]'));
        this.index = 0;
        this.interval = parseInt(this.getAttribute('data-interval'), 10) || 6000;
        this.autoplay = this.getAttribute('data-autoplay') === 'true' && !REDUCED.matches;
        this.hovering = false;
        this.focused = false;
        this.visible = true;
        this.held = false; /* theme editor: a selected slide stays put */
        /* Dots and arrows take the colour that reads on the part of the photo under them (Gilbert 20 Oct: dark on a
           light background and vice versa): a slide's own setting (data-controls-d / -m) wins, then the photo is
           read at this window's crop, then the slide's tone. */
        this.phone = window.matchMedia('(max-width: 749px)');
        if (!this.track || this.slides.length < 2) return;

        /* The current dot fills over the slide's time; the slide changes when the fill ends (Bambu-style timer). */
        this.style.setProperty('--hero-interval', this.interval + 'ms');
        this.classList.add(this.autoplay ? 'is-timing' : 'is-static');
        this.update();

        this.slideObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting && e.intersectionRatio >= 0.6) self.setIndex(self.slides.indexOf(e.target));
          });
        }, { root: this.track, threshold: [0.6] });
        this.slides.forEach(function (s) { self.slideObserver.observe(s); });

        this.viewObserver = new IntersectionObserver(function (entries) {
          self.visible = entries[0] ? entries[0].isIntersecting : true;
          self.syncPause();
        }, { threshold: 0.25 });
        this.viewObserver.observe(this);

        this.dots.forEach(function (dot, i) {
          dot.addEventListener('click', function () { self.go(i); });
          var fill = dot.querySelector('.cube-hero__dot-fill');
          if (fill) {
            fill.addEventListener('animationend', function (e) {
              if (e.animationName !== 'cubeHeroFill' || !self.autoplay || i !== self.index) return;
              self.go(self.index + 1);
            });
          }
        });

        /* Mouse (Gilbert 20 Oct: "I used to move the slides with the mouse"): a click-and-drag on the slides works like
           a swipe; no arrows (21 Oct: "Bambu doesn't have them"). Touch keeps the native swipe. */
        this.drag = null;
        this.track.addEventListener('pointerdown', function (e) {
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          self.drag = { id: e.pointerId, x: e.clientX, left: self.track.scrollLeft, from: self.index, moved: false };
        });
        this.track.addEventListener('pointermove', function (e) {
          var d = self.drag;
          if (!d || e.pointerId !== d.id) return;
          var dx = e.clientX - d.x;
          if (!d.moved) {
            if (Math.abs(dx) < 6) return;
            d.moved = true;
            try { self.track.setPointerCapture(d.id); } catch (err) { /* capture is optional */ }
            clearTimeout(self.dragTimer);
            self.classList.add('is-dragging');
          }
          self.track.scrollLeft = d.left - dx;
        });
        var endDrag = function (e) {
          var d = self.drag;
          if (!d || e.pointerId !== d.id) return;
          self.drag = null;
          if (!d.moved) return;
          var dx = e.clientX - d.x;
          var step = Math.abs(dx) > self.track.clientWidth * 0.1 ? (dx < 0 ? 1 : -1) : 0;
          /* The ends wrap: past the last slide comes the first, before the first comes the last (Gilbert 21 Oct). */
          var to = d.from + step;
          var last = self.slides.length - 1;
          self.go(to > last ? 0 : to < 0 ? last : to);
          /* A drag is not a click: swallow the click that follows this pointerup, then forget it. */
          self.justDragged = true;
          setTimeout(function () { self.justDragged = false; }, 0);
          /* Snap back on once the smooth scroll has landed on the slide. */
          var settle = function () {
            self.track.removeEventListener('scrollend', settle);
            self.classList.remove('is-dragging');
          };
          self.track.addEventListener('scrollend', settle);
          self.dragTimer = setTimeout(settle, 900);
        };
        this.track.addEventListener('pointerup', endDrag);
        this.track.addEventListener('pointercancel', endDrag);
        this.track.addEventListener('click', function (e) {
          if (self.justDragged) { e.preventDefault(); e.stopPropagation(); }
        }, true);
        this.track.addEventListener('dragstart', function (e) { e.preventDefault(); });
        /* Touch: the ends wrap, where the native swipe has nowhere to go (Gilbert 21 Oct: "it locks on the last one").
           A swipe left on the last slide goes to the first, a swipe right on the first goes to the last. */
        this.track.addEventListener('touchstart', function (e) {
          var t = e.touches[0];
          self.touch = { x: t.clientX, y: t.clientY, at: self.index };
        }, { passive: true });
        this.track.addEventListener('touchend', function (e) {
          var s = self.touch;
          self.touch = null;
          if (!s) return;
          var last = self.slides.length - 1;
          var t = e.changedTouches[0];
          var dx = t.clientX - s.x;
          if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(t.clientY - s.y)) return;
          if (dx < 0 && s.at === last && self.index === last) self.go(0);
          else if (dx > 0 && s.at === 0 && self.index === 0) self.go(last);
        }, { passive: true });

        // Hover pauses only for a real mouse (a tap fires emulated mouse events that would stick), and focus only
        // from the keyboard: a clicked or tapped dot must not hold the timer once the pointer leaves.
        this.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { self.hovering = true; self.syncPause(); } });
        this.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { self.hovering = false; self.syncPause(); } });
        this.addEventListener('focusin', function (e) {
          var keyboard = true;
          try { keyboard = e.target.matches(':focus-visible'); } catch (err) { /* old browser: keep pausing on focus */ }
          self.focused = keyboard;
          self.syncPause();
        });
        this.addEventListener('pointerdown', function () { if (self.focused) { self.focused = false; self.syncPause(); } });
        this.addEventListener('focusout', function (e) {
          if (!self.contains(e.relatedTarget)) { self.focused = false; self.syncPause(); }
        });
        this.onVisibility = function () { self.syncPause(); };
        document.addEventListener('visibilitychange', this.onVisibility);

        this.onBlockSelect = function (e) {
          var id = e.detail && e.detail.blockId;
          var slide = id && document.getElementById('CubeHero-' + id);
          if (!slide || !self.contains(slide)) return;
          self.held = true;
          self.syncPause();
          self.go(self.slides.indexOf(slide));
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.onLayout = function () {
          clearTimeout(self.layoutTimer);
          self.layoutTimer = setTimeout(function () { self.syncTone(); }, 200);
        };
        window.addEventListener('resize', this.onLayout);
        this.syncPause();
      }

      disconnectedCallback() {
        if (this.slideObserver) this.slideObserver.disconnect();
        if (this.viewObserver) this.viewObserver.disconnect();
        if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.onLayout) window.removeEventListener('resize', this.onLayout);
      }
      syncTone() {
        var current = this.slides[this.index];
        if (!current) return;
        var set = current.getAttribute(this.phone.matches ? 'data-controls-m' : 'data-controls-d');
        var els = this.controlEls();
        var read = set ? null : this.readTones(current, els);
        var row = set || (read && read.row) || (current.getAttribute('data-tone') === 'dark' ? 'white' : 'dark');
        this.classList.toggle('is-dark', row === 'white');
        /* A dot or arrow whose own patch of photo needs the other colour gets it (Cowork P8-003: on phones a dark
           printer sits under two of the dots on a light slide). */
        els.forEach(function (el, k) {
          var t = read && read.each[k];
          if (t && t !== row) {
            el.style.setProperty('--dot-rgb', t === 'white' ? '255, 255, 255' : '18, 18, 18');
            el.style.setProperty('--halo-rgb', t === 'white' ? '0, 0, 0' : '255, 255, 255');
          } else {
            el.style.removeProperty('--dot-rgb');
            el.style.removeProperty('--halo-rgb');
          }
        });
      }
      controlEls() {
        return Array.prototype.slice.call(this.querySelectorAll('[data-hero-dot]'))
          .filter(function (el) { return el.getBoundingClientRect().width > 0; });
      }
      /* Reads the photo under the dots row at this window's object-fit crop: the whole strip's median brightness picks
         the row colour ('white' on a dark strip, 'dark' on a light one) and each control's own patch picks its own.
         Null when it can't be read yet (the image re-syncs once it loads). Theme assets are served from the shop's
         own domain, so the canvas is not tainted; anything else falls back to the slide's tone. */
      readTones(slide, els) {
        var self = this;
        var img = slide.querySelector('img');
        if (!img || !els.length) return null;
        if (!img.complete || !img.naturalWidth) {
          if (!img.cubeWaiting) {
            img.cubeWaiting = true;
            img.addEventListener('load', function () {
              img.cubeWaiting = false;
              if (self.slides[self.index] === slide) self.syncTone();
            }, { once: true });
          }
          return null;
        }
        var hero = this.getBoundingClientRect();
        var sr = slide.getBoundingClientRect();
        var r = img.getBoundingClientRect();
        var boxes = els.map(function (el) { return el.getBoundingClientRect(); });
        var left = Math.min.apply(null, boxes.map(function (b) { return b.left; }));
        var right = Math.max.apply(null, boxes.map(function (b) { return b.right; }));
        var row = this.querySelector('.cube-hero__dots').getBoundingClientRect();
        var nw = img.naturalWidth;
        var nh = img.naturalHeight;
        var cs = getComputedStyle(img);
        var s = cs.objectFit === 'contain' ? Math.min(r.width / nw, r.height / nh) : Math.max(r.width / nw, r.height / nh);
        var pos = (cs.objectPosition || '50% 50%').split(' ');
        var off = function (v, free) { return /%$/.test(v) ? (free * parseFloat(v)) / 100 : parseFloat(v) || free / 2; };
        var ox = off(pos[0], r.width - nw * s);
        var oy = off(pos[1] || '50%', r.height - nh * s);
        /* The strip relative to the photo as it sits once its slide is in place (the track may be mid-scroll); only the
           middle band of the row, where the 7 px dots and the arrow glyphs actually are. */
        var x = left - hero.left - (r.left - sr.left) - ox;
        var y = row.top + row.height * 0.3 - r.top - oy;
        var sx = Math.max(0, x / s);
        var sy = Math.max(0, y / s);
        var sw = Math.min(nw - sx, (right - left) / s);
        var sh = Math.min(nh - sy, (row.height * 0.4) / s);
        if (sw <= 1 || sh <= 1) return null;
        var N = Math.max(8, Math.round((right - left) / 2));
        var data;
        try {
          var cv = document.createElement('canvas');
          cv.width = N;
          cv.height = 4;
          var ctx = cv.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, N, 4);
          data = ctx.getImageData(0, 0, N, 4).data;
        } catch (err) {
          return null;
        }
        var lin = function (v) { v /= 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
        var lum = function (i) { return 0.2126 * lin(data[i]) + 0.7152 * lin(data[i + 1]) + 0.0722 * lin(data[i + 2]); };
        /* 0.19: where white and near-black (#121212) give the same contrast. */
        var pick = function (c0, c1) {
          var L = [];
          for (var yy = 0; yy < 4; yy++) for (var xx = c0; xx < c1; xx++) L.push(lum((yy * N + xx) * 4));
          L.sort(function (a, b) { return a - b; });
          return L.length ? (L[L.length >> 1] < 0.19 ? 'white' : 'dark') : null;
        };
        return {
          row: pick(0, N),
          each: boxes.map(function (b) {
            var c0 = Math.max(0, Math.floor(((b.left - left) / (right - left)) * N));
            var c1 = Math.min(N, Math.max(c0 + 1, Math.ceil(((b.right - left) / (right - left)) * N)));
            return pick(c0, c1);
          })
        };
      }

      syncPause() {
        var paused = this.held || this.hovering || this.focused || !this.visible || document.hidden;
        this.classList.toggle('is-paused', paused);
      }

      go(i) {
        var n = this.slides.length;
        var k = ((i % n) + n) % n;
        var slide = this.slides[k];
        this.track.scrollTo({ left: slide.offsetLeft, behavior: REDUCED.matches ? 'auto' : 'smooth' });
        this.setIndex(k);
      }

      setIndex(i) {
        if (i < 0 || i === this.index) return;
        this.index = i;
        this.update();
      }

      update() {
        var self = this;
        this.slides.forEach(function (s, k) {
          var off = k !== self.index;
          if ('inert' in s) s.inert = off;
          s.setAttribute('aria-hidden', off ? 'true' : 'false');
        });
        this.dots.forEach(function (d, k) {
          if (k === self.index) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
        this.syncTone();
        clearTimeout(this.toneTimer);
        this.toneTimer = setTimeout(function () { self.syncTone(); }, 350);
        /* Restart the current dot's timer from zero. */
        var fill = this.dots[this.index] && this.dots[this.index].querySelector('.cube-hero__dot-fill');
        if (fill) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      }
    }
    customElements.define('cube-hero', CubeHero);
  }

  if (!customElements.get('cube-tabs')) {
    class CubeTabs extends HTMLElement {
      connectedCallback() {
        var self = this;
        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
        this.tabs.forEach(function (tab, i) {
          tab.addEventListener('click', function () { self.select(tab); });
          tab.addEventListener('keydown', function (e) {
            var n = self.tabs.length;
            var next = null;
            if (e.key === 'ArrowRight') next = self.tabs[(i + 1) % n];
            else if (e.key === 'ArrowLeft') next = self.tabs[(i - 1 + n) % n];
            else if (e.key === 'Home') next = self.tabs[0];
            else if (e.key === 'End') next = self.tabs[n - 1];
            if (next) {
              e.preventDefault();
              next.focus();
              self.select(next);
            }
          });
        });
        this.onBlockSelect = function (e) {
          var id = e.detail && e.detail.blockId;
          var tab = id && self.querySelector('#CubeShowTab-' + id);
          if (tab) self.select(tab);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      select(tab) {
        this.tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (tab.scrollIntoView) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
    customElements.define('cube-tabs', CubeTabs);
  }
})();
